import { ElementRef } from "@angular/core";

export function intersection(arr1: number[], arr2: number[]) {
  return arr1.filter((val1) => {
    return arr2.find((val2) => val1 === val2);
  });
}

export function round2Digits(m: number) {
  return Math.round(m * 100) / 100
}

export function isServer() {
  return ! (typeof window != 'undefined' && window.document)
}

export function scrollToFirstEl(
  elSel: string | ElementRef<HTMLElement> | HTMLElement | undefined,
  additionalMargin: number = 0,
  behavior: ScrollBehavior = "smooth"
) {
  if (isServer()) return
  if (! elSel) return

  let top = 0
  let el: HTMLElement
  if (elSel instanceof HTMLElement) {
    el = elSel as HTMLElement
  } else if (elSel instanceof ElementRef) {
    el = elSel.nativeElement
  } else {
    el = document.querySelector(elSel) as HTMLElement
  }

  if (! el) return

  if (el) top = getAbsoluteOffsetTop(el)
  else return

  el = document.querySelector('header') as HTMLElement
  if (el) top -= el.clientHeight

  el = document.querySelector('.category-settings') as HTMLElement
  if (el) top -= el.clientHeight

  top -= additionalMargin

  window.scrollTo({
    top: top,
    left: 0,
    behavior: behavior,
  })
}

function getAbsoluteOffsetTop(el: HTMLElement) {
  const bodyRect = document.body.getBoundingClientRect()
  const elemRect = el.getBoundingClientRect()
  let marginTop = 0
  const productContainerEl = document.querySelector('.product-container') as HTMLElement || undefined
  if (productContainerEl) {
    marginTop = 48
  }
  return elemRect.top - bodyRect.top - marginTop
}

export function mapNumberToLocaleString(number: number, ucfirst = false, female = false): string {
  let ls = ""
  switch (number) {
    case 1: ls = "Ein" + (female ? 'e' : '')
      break
    case 2: ls = "Zwei"
      break
    case 3: ls = "Drei"
      break
    case 4: ls = "Vier"
      break
    case 5: ls = "Fünf"
      break
    case 6: ls = "Sechs"
      break
    case 7: ls = "Sieben"
      break
    case 8: ls = "Acht"
      break
    case 9: ls = "Neun"
      break
    case 10: ls = "Zehn"
      break
    case 11: ls = "Elf"
      break
    case 12: ls = "Zwölf"
      break
  }

  if (! ucfirst) ls = ls.toLowerCase()

  return ls
}

/**
 * if n is negative this returns n, otherwise 0
 *
 * @param n
 */
export function positiveOrZero(n: number) {
  if (n > 0) return n
  return 0
}

// do not send requests to sentry when user-agent matches this regex
export const botRegex = /bot|crawler|spider|slurp|pingdom|uptime|curl/i

export function extractUserAgent(event: any): string {
  const headers = event?.request?.headers
  if (headers && typeof headers === "object") {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === "user-agent") {
        const v = headers[key]
        if (typeof v === "string" && v.length) return v
      }
    }
  }
  // Browser-SDK setzt request.headers["user-agent"] nicht; navigator.userAgent als Fallback
  if (typeof navigator !== "undefined" && typeof navigator.userAgent === "string") {
    return navigator.userAgent
  }
  return ""
}

/**
 * Erkennt nicht-behebbare "Maximum call stack size exceeded"-Fehler, die nicht
 * aus unserem eigenen Code stammen, sondern aus einem injizierten Fremd-Skript
 * (typischerweise Google Translate: verschachtelte <font><font>-Wrapper lösen im
 * DOM-Walker der Übersetzung eine unendliche wechselseitige Rekursion aus).
 *
 * WebKit (iOS) ordnet solche injizierten/ausgewerteten Skripte der Dokument-URL
 * zu (filename === Seiten-URL wie "/shop/", Funktionsnamen wie "Gk"/"Ik"), statt
 * einer echten Bundle-Datei. Echte App-Stackoverflows haben dagegen mindestens
 * einen Frame in einer echten Skript-Datei (*.js, z. B. main-*.js/chunk-*.js).
 *
 * Solche Events sind in unserem Code nicht behebbar und werden ausgefiltert.
 */
export function isNonActionableStackOverflow(event: any): boolean {
  const values = event?.exception?.values
  if (! Array.isArray(values) || values.length === 0) return false

  const hasStackOverflow = values.some((v: any) =>
    typeof v?.value === "string" &&
    /maximum call stack size exceeded/i.test(v.value)
  )
  if (! hasStackOverflow) return false

  const frames: any[] = []
  for (const v of values) {
    const fs = v?.stacktrace?.frames
    if (Array.isArray(fs)) frames.push(... fs)
  }
  if (frames.length === 0) return false

  const hasOwnScriptFrame = frames.some((f: any) => {
    const fn = f?.filename || f?.abs_path
    return typeof fn === "string" && /\.js(\?|#|$)/i.test(fn)
  })

  return ! hasOwnScriptFrame
}

export function sentryBeforeSend(event: any) {
  const ua = extractUserAgent(event)
  if (botRegex.test(ua)) {
    return null
  }
  if (isNonActionableStackOverflow(event)) {
    return null
  }
  return event
}

export function renderDateFromString(str?: string) {
  if (! str) return ""

  // is a datetime, but we handle it like a date
  let matches = str.match(/^(\d{4})-(\d\d)-(\d\d) \d\d:\d\d:\d\d$/)
  if (matches && matches.length == 4) {
    return matches[3] + '.' + matches[2] + '.' + matches[1]
  }

  // is a date
  matches = str.match(/^(\d{4})-(\d\d)-(\d\d)$/)
  if (matches && matches.length == 4) {
    return matches[3] + '.' + matches[2] + '.' + matches[1]
  }

  // is a utc datetime
  matches = str.match(/^(\d{4})-(\d\d)-(\d\d)T\d\d:\d\d:\d\d\.\d\d\d$/)
  if (matches && matches.length == 4) {
    return matches[3] + '.' + matches[2] + '.' + matches[1]
  }

  // did not match
  return str
}

export const CANONICAL_BASE_HREF_PREFIX = '/shop'

export function stripQueryAndFragment(url: string): string {
  const queryIdx = url.indexOf('?')
  const fragIdx = url.indexOf('#')
  let end = url.length
  if (queryIdx >= 0) end = Math.min(end, queryIdx)
  if (fragIdx >= 0) end = Math.min(end, fragIdx)
  return url.substring(0, end)
}

export function buildCanonicalPath(routerPath: string): string {
  const cleanPath = stripQueryAndFragment(routerPath || '/')
  const path = cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath
  const stripped = path.startsWith(CANONICAL_BASE_HREF_PREFIX)
    ? path.substring(CANONICAL_BASE_HREF_PREFIX.length)
    : path
  return stripped === '' || stripped === '/'
    ? CANONICAL_BASE_HREF_PREFIX
    : CANONICAL_BASE_HREF_PREFIX + stripped
}

export function resolveCanonicalBaseFromPublicHost(publicHost: string | null | undefined): string {
  if (!publicHost || publicHost.length === 0) {
    return ''
  }
  const trimmed = publicHost.replace(/\/+$/, '')
  return ensureHttps(trimmed)
}

export function ensureHttps(url: string): string {
  if (!url) return url
  if (/^https:\/\//i.test(url)) return url
  if (/^http:\/\//i.test(url)) return 'https://' + url.substring('http://'.length)
  return 'https://' + url
}

export function buildCanonicalHref(base: string, routerPath: string): string {
  return base + buildCanonicalPath(routerPath)
}

/**
 * Liefert einen nicht-leeren, zugänglichen Linktext für einen
 * Footer-Sitemap-Eintrag. Ist der aus dem CMS gelieferte Text leer, wird ein
 * lesbares Fallback-Label aus dem letzten URL-Segment abgeleitet, damit keine
 * Links ohne Textinhalt entstehen (Barrierefreiheit, WCAG 2.4.4 / 4.1.2).
 */
export function resolveFooterLinkLabel(text: string | null | undefined, url: string | null | undefined): string {
  if (text && text.trim().length) return text
  const cleanUrl = stripQueryAndFragment(url || '').replace(/\/+$/, '')
  const slug = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1)
  if (! slug) return 'Mehr erfahren'
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Ermittelt die Canonical-URL für eine Kategorie-Seite. Hat die aktuelle
 * Kategorie keine (nicht-leere) Beschreibung, wird der Pfad der Hauptkategorie
 * verwendet, sofern vorhanden. Andernfalls der Pfad der Kategorie selbst.
 */
export function resolveCategoryCanonicalPath(
  currentFullPath: string,
  currentDescription: string | null | undefined,
  mainFullPath: string | null | undefined,
): string {
  const hasDescription = !! (currentDescription && currentDescription.trim().length)
  if (! hasDescription && mainFullPath) {
    return mainFullPath
  }
  return currentFullPath
}

export function resolveCategoryErrorStatus(code: number): 404 | 503 {
  return code === 0 || (code >= 500 && code <= 599) ? 503 : 404
}

export function shouldRenderPageError(
  status: 404 | 503,
  isBrowser: boolean,
  wasRenderedBySsr: boolean = false,
): boolean {
  return status === 404 || ! isBrowser || wasRenderedBySsr
}
